/* BloxShop Theme JS v2 — Full AJAX cart, quick-add, search, modals */
'use strict';

/* ─────────────────────────────────────────
   UTILITIES
───────────────────────────────────────── */
function money(cents) {
  return '$' + (cents / 100).toFixed(2);
}
function esc(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}
async function shopifyFetch(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(opts.headers || {}) }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Request failed');
  }
  return res.json();
}

/* ─────────────────────────────────────────
   CART
───────────────────────────────────────── */
const BSCart = (() => {
  let isOpen = false;

  async function getCart() {
    return shopifyFetch('/cart.js');
  }

  function updateBadge(count) {
    const badge = document.getElementById('bs-cart-count');
    if (!badge) return;
    badge.textContent = count;
    if (count > 0) {
      badge.removeAttribute('hidden');
      badge.animate([{transform:'scale(0.5)'},{transform:'scale(1.3)'},{transform:'scale(1)'}], {duration:300});
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  /*
    Cart state machine.
    IMPORTANT: must removeAttribute('hidden') BEFORE setting style.display,
    because [hidden]{display:none!important} in CSS beats inline style.display.
  */
  function cartHide(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
    el.style.display = 'none';
  }
  function cartShow(el, d) {
    if (!el) return;
    el.removeAttribute('hidden');      // remove !important CSS rule first
    el.style.display = d || 'block';   // then set display
  }

  function setCartState(state, cart) {
    var loadingEl  = document.getElementById('bs-cart-loading');
    var itemsEl    = document.getElementById('bs-cart-items');
    var emptyEl    = document.getElementById('bs-cart-empty');
    var footerEl   = document.getElementById('bs-cart-footer');
    var subtotalEl = document.getElementById('bs-cart-subtotal');

    // Hide all zones first
    cartHide(loadingEl);
    cartHide(itemsEl);
    cartHide(emptyEl);
    cartHide(footerEl);

    if (state === 'loading') {
      cartShow(loadingEl, 'flex');
      return;
    }

    if (state === 'empty' || !cart || cart.item_count === 0) {
      cartShow(emptyEl, 'flex');
      return;
    }

    // state === 'items' with items
    var hasFeeService = typeof BSServiceFee !== 'undefined';
    // Always detect and hide fee lines from items list regardless of enabled flag
    var feeItem = hasFeeService
      ? cart.items.find(function(i){ return BSServiceFee.isFeeLine(i); })
      : null;
    var displayItems = cart.items.filter(function(i){
      return !(hasFeeService && feeItem && BSServiceFee.isFeeLine(i));
    });
    // Show fee whenever the service fee module has a handle configured
    var feeEnabled = hasFeeService && !!(BSServiceFee.handle);

    cartShow(itemsEl, 'flex');
    itemsEl.innerHTML = displayItems.map(function(item) {
      // Service fee lines get special locked rendering
      var isFee = typeof BSServiceFee !== 'undefined' && BSServiceFee.isFeeLine(item);
      var tooltip = isFee ? ((window.BSServiceFeeSettings || {}).tooltip || 'Service fee') : '';
      return '<div class="bs-cart-item' + (isFee ? ' bs-cart-item--fee' : '') + '" data-key="' + esc(item.key) + '">' +
        (isFee ? '' :
          '<div class="bs-cart-item__img">' +
            (item.image ? '<img src="' + item.image + '" alt="' + esc(item.product_title) + '" width="68" height="68" loading="lazy">' : '') +
          '</div>'
        ) +
        '<div class="bs-cart-item__info">' +
          (isFee
            ? '<span class="bs-cart-item__title bs-cart-item__fee-label">Service Fee ' +
                '<span class="bs-fee-tooltip" tabindex="0" aria-label="' + esc(tooltip) + '">' +
                  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
                  '<span class="bs-fee-tooltip__text">' + esc(tooltip) + '</span>' +
                '</span>' +
              '</span>'
            : '<a href="' + item.url + '" class="bs-cart-item__title">' + esc(item.product_title) + '</a>'
          ) +
          ((!isFee && item.variant_title && item.variant_title !== 'Default Title') ? '<p class="bs-cart-item__variant">' + esc(item.variant_title) + '</p>' : '') +
          '<div class="bs-cart-item__row">' +
            '<span class="bs-price' + (isFee ? ' bs-fee-price' : ' bs-price--sale') + '">' + money(item.final_line_price) + '</span>' +
            (isFee ? '' :
              '<div class="bs-cart-item__qty">' +
                '<button class="bs-cart-item__qty-btn" data-change="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease">&#8722;</button>' +
                '<span class="bs-cart-item__qty-val">' + item.quantity + '</span>' +
                '<button class="bs-cart-item__qty-btn" data-change="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
              '</div>'
            ) +
          '</div>' +
          (isFee ? '' : '<button class="bs-cart-item__remove" data-change="' + esc(item.key) + '" data-qty="0">Remove</button>') +
        '</div>' +
      '</div>';
    }).join('');

    cartShow(footerEl, 'block');
    // Attach checkout via event listener (safer than inline onclick for CSP)
    var chkBtn = document.getElementById('bs-checkout-btn');
    if (chkBtn && !chkBtn._bsCheckoutBound) {
      chkBtn._bsCheckoutBound = true;
      chkBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (typeof BSDiscount !== 'undefined') {
          BSDiscount.checkout();
        } else {
          window.location.href = '/checkout';
        }
      });
    }

    // Footer: show subtotal of real items + service fee line separately
    var realSubtotal = displayItems.reduce(function(s,i){ return s + i.final_line_price; }, 0);
    // Show service fee: use actual cart line if present, else show calculated estimate
    var feeLineHtml = '';
    if (feeEnabled && realSubtotal > 0) {
      var feeTip  = ((window.BSServiceFeeSettings || {}).tooltip || 'Service fee helps us keep prices low.');
      var feeRate = ((window.BSServiceFeeSettings || {}).rate || 5);
      // Prefer actual charged amount from cart; fall back to calculated estimate
      var feeAmountCents = feeItem
        ? feeItem.final_line_price
        : (typeof BSServiceFee !== 'undefined' && BSServiceFee.calcFeeCents
            ? BSServiceFee.calcFeeCents(realSubtotal)
            : Math.max(50, Math.floor(realSubtotal * feeRate / 10000) * 50));
      if (feeAmountCents > 0) {
        feeLineHtml = '<div class="bs-cart-fee-summary">' +
          '<span class="bs-cart-fee-summary__label">Service fee (' + feeRate + '%)' +
          ' <span class="bs-fee-tooltip" tabindex="0">' +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
            '<span class="bs-fee-tooltip__text">' + esc(feeTip) + '</span>' +
          '</span></span>' +
          '<span class="bs-cart-fee-summary__amount">' + money(feeAmountCents) + '</span>' +
        '</div>';
      }
    }
    var feeContainer = document.getElementById('bs-cart-fee-container');
    if (feeContainer) feeContainer.innerHTML = feeLineHtml;

    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
  }

  function renderItems(cart) { setCartState('items', cart); }


  async function refresh() {
    try {
      const cart = await getCart();
      updateBadge(cart.item_count);
      if (isOpen) setCartState('items', cart);

      // Service fee: update if enabled, then re-render with updated cart
      if (typeof BSServiceFee !== 'undefined' && BSServiceFee.enabled) {
        const changed = await BSServiceFee.update(cart);
        if (changed) {
          const updatedCart = await getCart();
          updateBadge(updatedCart.item_count);
          if (isOpen) setCartState('items', updatedCart);
          return updatedCart;
        }
      }
      return cart;
    } catch(e) {
      console.error('BSCart.refresh', e);
      if (isOpen) setCartState('empty');
    }
  }

  async function change(key, qty) {
    try {
      const cart = await shopifyFetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      });
      updateBadge(cart.item_count);
      setCartState('items', cart);
      // Recalculate service fee whenever quantity changes
      if (typeof BSServiceFee !== 'undefined' && BSServiceFee.enabled) {
        const changed = await BSServiceFee.update(cart);
        if (changed) {
          const updatedCart = await getCart();
          updateBadge(updatedCart.item_count);
          if (isOpen) setCartState('items', updatedCart);
        }
      }
    } catch(e) { console.error('BSCart.change', e); }
  }

  function open() {
    isOpen = true;
    const drawer  = document.getElementById('bs-cart-drawer');
    const overlay = document.getElementById('bs-cart-overlay');
    if (drawer)  { drawer.classList.add('open');  drawer.removeAttribute('aria-hidden'); drawer.focus(); }
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Show loading only if no items already rendered
    const itemsEl = document.getElementById('bs-cart-items');
    const hasRenderedItems = itemsEl && itemsEl.style.display !== 'none' && itemsEl.children.length > 0;
    if (!hasRenderedItems) setCartState('loading');
    refresh();
    // Restore saved Roblox username
    const usernameInput = document.getElementById('bs-roblox-username');
    if (usernameInput) usernameInput.value = sessionStorage.getItem('bs_roblox_user') || '';
  }

  function close() {
    isOpen = false;
    const drawer  = document.getElementById('bs-cart-drawer');
    const overlay = document.getElementById('bs-cart-overlay');
    if (drawer)  { drawer.classList.remove('open'); drawer.setAttribute('aria-hidden','true'); }
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  // Event delegation for qty/remove buttons
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-change]');
    if (!btn) return;
    change(btn.dataset.change, parseInt(btn.dataset.qty, 10));
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  return { open, close, change, refresh, updateBadge };
})();

/* ─────────────────────────────────────────
   TOAST
───────────────────────────────────────── */
const BSToast = (() => {
  let timer;
  function show(title) {
    const toast = document.getElementById('bs-toast');
    const prod  = document.getElementById('bs-toast-product');
    if (!toast) return;
    if (prod) prod.textContent = title;
    toast.classList.add('show');
    clearTimeout(timer);
    timer = setTimeout(hide, 4000);
  }
  function hide() {
    const toast = document.getElementById('bs-toast');
    if (toast) toast.classList.remove('show');
  }
  return { show, hide };
})();

/* ─────────────────────────────────────────
   QUICK ADD
───────────────────────────────────────── */
const BSQuickAdd = (() => {
  function setBtn(btn, state, label) {
    if (!btn) return;
    const states = {
      idle:    `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>`,
      loading: `<div class="bs-spinner" style="width:13px;height:13px;border-width:2px;"></div>`,
      success: `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    };
    btn.innerHTML = states[state] || states.idle;
    btn.disabled  = state === 'loading';
    btn.classList.toggle('bs-qadd--loading',  state === 'loading');
    btn.classList.toggle('bs-qadd--success',  state === 'success');
    if (label) btn.setAttribute('aria-label', label);
  }

  async function addVariant(variantId, qty, btn, title) {
    setBtn(btn, 'loading');
    try {
      await shopifyFetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty || 1 })
      });
      setBtn(btn, 'success');
      BSCart.refresh();
      BSToast.show(title || 'Item');
      BSCart.open();
      setTimeout(() => setBtn(btn, 'idle'), 2000);
    } catch(e) {
      console.error('BSQuickAdd', e);
      setBtn(btn, 'idle');
      alert(e.message || 'Could not add to cart. Please try again.');
    }
  }

  // Event delegation — works for dynamically rendered cards too
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    // Product page ATC — use qty input
    if (btn.dataset.pageAtc) {
      const qty = parseInt(document.getElementById('bs-qty')?.value || '1', 10);
      const vid = document.getElementById('bs-product-select')?.value || btn.dataset.variantId;
      await addVariant(vid, qty, btn, btn.dataset.productTitle);
      return;
    }

    const variantCount = parseInt(btn.dataset.variantCount || '1', 10);
    const title = btn.dataset.productTitle || 'Item';

    if (variantCount > 1) {
      // Multiple variants — open selector
      BSVariantModal.open(btn);
    } else {
      await addVariant(btn.dataset.variantId, 1, btn, title);
    }
  });

  return { addVariant, setBtn };
})();

/* ─────────────────────────────────────────
   VARIANT MODAL
───────────────────────────────────────── */
const BSVariantModal = (() => {
  let currentBtn = null;
  let selectedVariantId = null;

  async function open(triggerBtn) {
    currentBtn = triggerBtn;
    selectedVariantId = null;

    const modal    = document.getElementById('bs-variant-modal');
    const titleEl  = document.getElementById('bs-vm-title');
    const priceEl  = document.getElementById('bs-vm-price');
    const optsEl   = document.getElementById('bs-vm-options');
    const imgWrap  = document.getElementById('bs-vm-img-wrap');
    if (!modal) return;

    // Fetch product variants via AJAX
    const handle = triggerBtn.dataset.productHandle;
    if (!handle) return;

    try {
      const data = await shopifyFetch(`/products/${handle}.js`);
      const title = data.title;

      if (titleEl) titleEl.textContent = title;
      if (priceEl) priceEl.textContent = money(data.price);

      // Product image
      if (imgWrap) {
        imgWrap.innerHTML = data.featured_image
          ? `<img src="${data.featured_image}" alt="${esc(title)}" width="56" height="56" loading="lazy">`
          : '';
      }

      // Variant options
      if (optsEl) {
        optsEl.innerHTML = data.variants.map(v => `
          <button class="bs-variant-pill${!v.available ? ' bs-variant-pill--disabled' : ''}"
                  data-variant-id="${v.id}"
                  data-variant-price="${v.price}"
                  ${!v.available ? 'disabled' : ''}>
            ${esc(v.title)}${!v.available ? ' <small style="opacity:.55">— Sold Out</small>' : ''}
          </button>
        `).join('');

        // Select first available
        const firstAvail = data.variants.find(v => v.available);
        if (firstAvail) selectVariant(optsEl.querySelector(`[data-variant-id="${firstAvail.id}"]`));
      }

      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
    } catch(e) {
      console.error('BSVariantModal.open', e);
    }
  }

  function selectVariant(btn) {
    if (!btn) return;
    document.querySelectorAll('#bs-vm-options .bs-variant-pill').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    selectedVariantId = btn.dataset.variantId;
    const priceEl = document.getElementById('bs-vm-price');
    if (priceEl && btn.dataset.variantPrice) priceEl.textContent = money(parseInt(btn.dataset.variantPrice, 10));
  }

  function close() {
    const modal = document.getElementById('bs-variant-modal');
    if (modal) modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    currentBtn = null;
    selectedVariantId = null;
  }

  async function addSelected() {
    if (!selectedVariantId) { alert('Please select an option.'); return; }
    const title = currentBtn?.dataset.productTitle || 'Item';
    const addBtn = document.getElementById('bs-vm-add-btn');
    close();
    await BSQuickAdd.addVariant(selectedVariantId, 1, currentBtn, title);
  }

  // Delegated click on variant pills in modal
  document.addEventListener('click', e => {
    const pill = e.target.closest('#bs-vm-options .bs-variant-pill');
    if (pill) selectVariant(pill);
  });

  return { open, close, addSelected };
})();

/* ─────────────────────────────────────────
   GAME SELECTION MODAL
───────────────────────────────────────── */
const BSModal = (() => {
  function open() {
    const modal = document.getElementById('bs-game-modal');
    if (modal) { modal.removeAttribute('hidden'); document.body.style.overflow = 'hidden'; }
  }
  function close() {
    const modal = document.getElementById('bs-game-modal');
    if (modal) { modal.setAttribute('hidden', ''); document.body.style.overflow = ''; }
  }
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  return { open, close };
})();

/* ─────────────────────────────────────────
   HEADER DROPDOWN
───────────────────────────────────────── */
const BSDropdown = (() => {
  function init() {
    const btn  = document.getElementById('bs-game-btn');
    const menu = document.getElementById('bs-game-menu');
    const wrap = document.querySelector('.bs-dropdown');
    if (!btn || !menu) return;

    btn.addEventListener('click', e => {
      e.stopPropagation();
      const open = menu.classList.toggle('open');
      wrap?.toggleAttribute('data-open', open);
      btn.setAttribute('aria-expanded', String(open));
      menu.setAttribute('aria-hidden', String(!open));
    });

    document.addEventListener('click', () => {
      menu.classList.remove('open');
      wrap?.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    });

    menu.addEventListener('click', e => e.stopPropagation());
  }
  return { init };
})();

/* ─────────────────────────────────────────
   MOBILE MENU
───────────────────────────────────────── */
const BSMobileMenu = (() => {
  function init() {
    const burger = document.getElementById('bs-burger');
    const menu   = document.getElementById('bs-mobile-menu');
    if (!burger || !menu) return;
    burger.addEventListener('click', () => {
      const open = menu.classList.toggle('open');
      burger.classList.toggle('open', open);
      burger.setAttribute('aria-expanded', String(open));
      menu.setAttribute('aria-hidden', String(!open));
    });
  }
  return { init };
})();

/* ─────────────────────────────────────────
   SEARCH
───────────────────────────────────────── */
const BSSearch = (() => {
  let debounce;

  function open() {
    const overlay = document.getElementById('bs-search-overlay');
    if (overlay) { overlay.removeAttribute('hidden'); document.body.style.overflow = 'hidden'; }
    setTimeout(() => document.getElementById('bs-search-input')?.focus(), 80);
  }

  function close() {
    const overlay = document.getElementById('bs-search-overlay');
    if (overlay) overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  async function search(q) {
    const resultsEl = document.getElementById('bs-search-results');
    if (!resultsEl) return;
    q = q.trim();
    if (q.length < 2) { resultsEl.innerHTML = ''; return; }

    resultsEl.innerHTML = '<div class="bs-search-empty" style="padding:20px 0;"><div class="bs-spinner bs-spinner--blue" style="margin:0 auto;"></div></div>';

    try {
      // Use plain fetch (no Content-Type header) — shopifyFetch sends Content-Type:application/json
      // which causes Shopify's predictive search to return HTML instead of JSON on GET requests
      const resp = await fetch(
        '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8',
        { headers: { 'Accept': 'application/json' } }
      );
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json();

      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];

      if (!products.length) {
        resultsEl.innerHTML =
          '<div class="bs-search-empty">No results for <strong>' + esc(q) + '</strong>' +
          ' &mdash; <a href="/search?q=' + encodeURIComponent(q) + '">Full search</a></div>';
        return;
      }

      resultsEl.innerHTML = products.map(function(p) {
        // image can be a URL string or an object with .url/.src depending on Shopify version
        var imgSrc = '';
        if (p.image) {
          imgSrc = typeof p.image === 'string' ? p.image : (p.image.src || p.image.url || '');
        } else if (p.featured_image) {
          imgSrc = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.url || '');
        }
        // p.price is a string like "9.99" — convert to Shopify money format
        var priceStr = '';
        if (p.price) {
          var cents = parseFloat(p.price) * 100;
          priceStr = money(isNaN(cents) ? 0 : cents);
        }
        return '<a href="' + p.url + '" class="bs-search-result-item" onclick="BSSearch.close()">' +
          '<div class="bs-search-result-item__img">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + esc(p.title) + '" width="52" height="52" loading="lazy">' : '') +
          '</div>' +
          '<div class="bs-search-result-item__body">' +
            '<p class="bs-search-result-item__title">' + esc(p.title) + '</p>' +
            (priceStr ? '<p class="bs-search-result-item__price">' + priceStr + '</p>' : '') +
          '</div>' +
        '</a>';
      }).join('') +
      '<a href="/search?q=' + encodeURIComponent(q) + '" class="bs-search-view-all" onclick="BSSearch.close()">View all results →</a>';

    } catch(e) {
      console.error('BSSearch', e);
      resultsEl.innerHTML =
        '<div class="bs-search-empty">Search unavailable. ' +
        '<a href="/search?q=' + encodeURIComponent(q) + '">Try full search</a></div>';
    }
  }

  function init() {
    const input = document.getElementById('bs-search-input');
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => search(input.value), 260);
      });
    }
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  return { open, close, init };
})();

/* ─────────────────────────────────────────
   FAQ ACCORDION
───────────────────────────────────────── */
const BSFAQ = (() => {
  function init() {
    document.querySelectorAll('[data-faq-trigger]').forEach(btn => {
      btn.addEventListener('click', () => {
        const answer  = btn.nextElementSibling;
        const isOpen  = btn.getAttribute('aria-expanded') === 'true';

        // Close all others
        document.querySelectorAll('[data-faq-trigger][aria-expanded="true"]').forEach(b => {
          if (b !== btn) {
            b.setAttribute('aria-expanded', 'false');
            b.nextElementSibling?.setAttribute('hidden', '');
            b.nextElementSibling?.style && (b.nextElementSibling.style.maxHeight = '0');
          }
        });

        btn.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) {
          answer?.setAttribute('hidden', '');
          if (answer) answer.style.maxHeight = '0';
        } else {
          answer?.removeAttribute('hidden');
          if (answer) answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  }
  return { init };
})();

/* ─────────────────────────────────────────
   PRODUCT PAGE
───────────────────────────────────────── */
const BSProduct = (() => {
  function switchImage(src, thumbEl) {
    const img = document.getElementById('bs-product-main-img');
    if (img) img.src = src;
    document.querySelectorAll('.bs-product-page__thumb').forEach(t => t.classList.remove('active'));
    if (thumbEl) thumbEl.classList.add('active');
  }

  function adjustQty(delta) {
    const input = document.getElementById('bs-qty');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
  }

  function selectOption(btn, optionIndex) {
    const group = btn.closest('.bs-variant-pills');
    if (group) {
      group.querySelectorAll('.bs-variant-pill').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
    }

    // Find matching variant
    if (!window.BSProductData?.variants) return;
    const selected = [];
    document.querySelectorAll('.bs-variant-pills').forEach((g, i) => {
      const active = g.querySelector('.bs-variant-pill.active');
      selected[i] = active?.dataset.value;
    });

    const match = window.BSProductData.variants.find(v =>
      v.options.every((opt, i) => !selected[i] || opt === selected[i])
    );

    if (match) {
      const select = document.getElementById('bs-product-select');
      if (select) select.value = match.id;

      // Update ATC button
      const atcBtn = document.getElementById('bs-product-atc');
      if (atcBtn) {
        atcBtn.dataset.variantId = match.id;
        atcBtn.disabled = !match.available;
        atcBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      }

      // Update price display
      const priceEl = document.querySelector('.bs-product-page__price .bs-price');
      if (priceEl) priceEl.textContent = money(match.price);
    }
  }

  return { switchImage, adjustQty, selectOption };
})();

/* ─────────────────────────────────────────
   LIVE ORDER NOTIFICATION
───────────────────────────────────────── */
const BSLiveOrder = (() => {
  let products = [];
  let idx = 0;
  let timer;

  function show() {
    if (!products.length) return;
    const p   = products[idx % products.length];
    idx++;
    const el  = document.getElementById('bs-live-order');
    const txt = document.getElementById('bs-lo-text');
    const img = document.getElementById('bs-lo-img-wrap');
    if (!el) return;
    if (txt) txt.textContent = 'Someone just purchased ' + p.title;
    if (img) img.innerHTML = p.image ? `<img src="${p.image}" alt="${esc(p.title)}" width="44" height="44" loading="lazy">` : '';
    el.removeAttribute('hidden');
    el.classList.add('show');
    setTimeout(hide, 5000);
  }

  function hide() {
    const el = document.getElementById('bs-live-order');
    if (el) el.classList.remove('show');
  }

  function init() {
    const el = document.getElementById('bs-live-order');
    if (!el) return;
    try { products = JSON.parse(el.dataset.products || '[]'); } catch(e) {}
    if (!products.length) return;
    const interval = (parseInt(el.dataset.interval, 10) || 15) * 1000;
    setTimeout(() => { show(); timer = setInterval(show, interval); }, 7000);
  }

  return { show, hide, init };
})();

/* ─────────────────────────────────────────
   INIT
───────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  BSCart.refresh();
  BSDropdown.init();
  BSMobileMenu.init();
  BSSearch.init();
  BSFAQ.init();
  BSLiveOrder.init();
});

/* ==========================================================================
   GAME TABS — vanilla JS, scoped, no external deps
   ========================================================================== */
(function() {
  'use strict';

  function initGameTabs(section) {
    var pills  = section.querySelectorAll('.bs-game-tabs__pill');
    var panels = section.querySelectorAll('.bs-game-tabs__panel');
    if (!pills.length) return;

    pills.forEach(function(pill) {
      pill.addEventListener('click', function() {
        var tabId = pill.getAttribute('data-tab');

        // Update pills
        pills.forEach(function(p) {
          p.classList.remove('active');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-selected', 'true');

        // Update panels
        panels.forEach(function(panel) {
          panel.classList.remove('active');
          panel.setAttribute('aria-hidden', 'true');
        });
        var target = section.querySelector('#bs-tab-' + tabId);
        if (target) {
          target.classList.add('active');
          target.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  // Init all game-tab sections on page
  function initAll() {
    document.querySelectorAll('[data-game-tabs]').forEach(initGameTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();

/* ==========================================================================
   QUICK-ADD: extend to handle bs-qadd-full buttons
   The existing BSQuickAdd already uses event delegation on [data-quick-add],
   so bs-qadd-full buttons work automatically. We just need spinner/success
   states for the labeled button.
   ========================================================================== */
(function() {
  'use strict';

  // Override setBtn to also handle full-width labeled buttons
  var _origSetBtn = (typeof BSQuickAdd !== 'undefined') ? BSQuickAdd.setBtn : null;

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.bs-qadd-full[data-quick-add]');
    if (!btn || btn.disabled) return;
    // The main BSQuickAdd delegation handles the actual cart logic.
    // Here we just ensure loading/success visual for the full button.
    // Loading: set text
    var originalHTML = btn.innerHTML;
    btn.__originalHTML = originalHTML;
  }, true); // capture phase so we run before BSQuickAdd

  // Patch BSQuickAdd.setBtn if available
  function patchSetBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function(btn, state, label) {
      if (!btn) return;
      // Full-width labeled button
      if (btn.classList.contains('bs-qadd-full')) {
        var states = {
          idle:    btn.__originalHTML || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="white" stroke-width="2"/><path d="M16 10a4 4 0 01-8 0" stroke="white" stroke-width="2" stroke-linecap="round"/></svg> Add to Cart',
          loading: '<div class="bs-spinner" style="width:14px;height:14px;border-width:2px;margin:0 auto;"></div>',
          success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Added!'
        };
        if (!btn.__originalHTML && state === 'loading') btn.__originalHTML = btn.innerHTML;
        btn.innerHTML = states[state] || states.idle;
        btn.disabled  = state === 'loading';
        btn.classList.toggle('bs-qadd-full--loading',  state === 'loading');
        if (state === 'success') {
          btn.style.background = 'var(--green)';
          setTimeout(function() {
            btn.style.background = '';
            btn.innerHTML = btn.__originalHTML;
          }, 1800);
        } else {
          btn.style.background = '';
        }
        return;
      }
      // Fall back to original for circle buttons
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchSetBtn);
  } else {
    setTimeout(patchSetBtn, 0); // after bloxshop.js BSQuickAdd init
  }
})();

/* ==========================================================================
   HERO TEXT ROTATOR — slot-machine style
   ========================================================================== */
(function () {
  'use strict';

  function initRotator() {
    var display = document.getElementById('bs-rotator-display') || document.querySelector('#bs-rotator-words .bs-rotator-word');
    if (!display) return;

    var raw = display.getAttribute('data-words') || '';
    var texts = raw.split(',').map(function(t){ return t.trim(); }).filter(Boolean);
    if (texts.length < 2) return;

    // Pre-measure widest word for stable container width
    var container = document.getElementById('bs-hero-rotator');
    if (container) {
      var maxW = 0;
      texts.forEach(function(t) {
        display.textContent = t;
        var w = display.offsetWidth || display.scrollWidth;
        if (w > maxW) maxW = w;
      });
      if (maxW > 0) container.style.minWidth = (maxW + 4) + 'px';
    }

    display.textContent = texts[0];
    var idx = 0;

    var timer = setInterval(function() {
      idx = (idx + 1) % texts.length;
      display.textContent = texts[idx];
    }, 2000);

    if (container) {
      container.addEventListener('mouseenter', function(){ clearInterval(timer); });
      container.addEventListener('mouseleave', function(){
        timer = setInterval(function(){
          idx = (idx + 1) % texts.length;
          display.textContent = texts[idx];
        }, 2000);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRotator);
  } else {
    initRotator();
  }
})();

/* ==========================================================================
   QUICK-ADD: extend to handle bs-qadd-full buttons
   The existing BSQuickAdd already uses event delegation on [data-quick-add],
   so bs-qadd-full buttons work automatically. We just need spinner/success
   states for the labeled button.
   ========================================================================== */
(function() {
  'use strict';

  // Override setBtn to also handle full-width labeled buttons
  var _origSetBtn = (typeof BSQuickAdd !== 'undefined') ? BSQuickAdd.setBtn : null;

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.bs-qadd-full[data-quick-add]');
    if (!btn || btn.disabled) return;
    // The main BSQuickAdd delegation handles the actual cart logic.
    // Here we just ensure loading/success visual for the full button.
    // Loading: set text
    var originalHTML = btn.innerHTML;
    btn.__originalHTML = originalHTML;
  }, true); // capture phase so we run before BSQuickAdd

  // Patch BSQuickAdd.setBtn if available
  function patchSetBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function(btn, state, label) {
      if (!btn) return;
      // Full-width labeled button
      if (btn.classList.contains('bs-qadd-full')) {
        var states = {
          idle:    btn.__originalHTML || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="white" stroke-width="2"/><path d="M16 10a4 4 0 01-8 0" stroke="white" stroke-width="2" stroke-linecap="round"/></svg> Add to Cart',
          loading: '<div class="bs-spinner" style="width:14px;height:14px;border-width:2px;margin:0 auto;"></div>',
          success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Added!'
        };
        if (!btn.__originalHTML && state === 'loading') btn.__originalHTML = btn.innerHTML;
        btn.innerHTML = states[state] || states.idle;
        btn.disabled  = state === 'loading';
        btn.classList.toggle('bs-qadd-full--loading',  state === 'loading');
        if (state === 'success') {
          btn.style.background = 'var(--green)';
          setTimeout(function() {
            btn.style.background = '';
            btn.innerHTML = btn.__originalHTML;
          }, 1800);
        } else {
          btn.style.background = '';
        }
        return;
      }
      // Fall back to original for circle buttons
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchSetBtn);
  } else {
    setTimeout(patchSetBtn, 0); // after bloxshop.js BSQuickAdd init
  }
})();

/* ==========================================================================
   HERO TEXT ROTATOR — slot-machine style
   ========================================================================== */
(function () {
  'use strict';

  function initRotator() {
    var wordSpans = document.querySelectorAll('#bs-rotator-words .bs-rotator-word');
    if (!wordSpans || wordSpans.length < 2) return;

    // Collect all word texts from existing spans
    var texts = [];
    wordSpans.forEach(function(w) { texts.push(w.textContent.trim()); });

    // KEEP the first existing span visible (it already has gradient CSS applied)
    // just hide spans 2-N and reuse span 0 as the display element
    var display = wordSpans[0];
    for (var i = 1; i < wordSpans.length; i++) {
      wordSpans[i].style.display = 'none';
    }

    // Fix container width to the widest word (measure before hiding)
    var container = document.getElementById('bs-hero-rotator');
    if (container) {
      var maxW = 0;
      // All spans still in DOM — measure each
      for (var i = 0; i < wordSpans.length; i++) {
        wordSpans[i].style.display = 'inline';
        var w = wordSpans[i].offsetWidth || wordSpans[i].scrollWidth || 0;
        if (w > maxW) maxW = w;
        wordSpans[i].style.display = i === 0 ? 'inline' : 'none';
      }
      if (maxW > 0) container.style.minWidth = (maxW + 4) + 'px';
    }

    var idx = 0;
    var timer = setInterval(function() {
      idx = (idx + 1) % texts.length;
      display.textContent = texts[idx];
    }, 2000);

    if (container) {
      container.addEventListener('mouseenter', function () { clearInterval(timer); });
      container.addEventListener('mouseleave', function () { timer = setInterval(function() {
        idx = (idx + 1) % texts.length;
        display.textContent = texts[idx];
      }, 2000); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRotator);
  } else {
    initRotator();
  }
})();

/* ==========================================================================
   FLOAT CARD ATC — extend BSQuickAdd.setBtn for .bs-float-card__atc
   ========================================================================== */
(function () {
  'use strict';

  function patchFloatCardBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function (btn, state, label) {
      if (!btn) return;
      if (btn.classList.contains('bs-float-card__atc')) {
        var saved = btn.__savedHTML || btn.innerHTML;
        if (state === 'loading') {
          btn.__savedHTML = saved;
          btn.innerHTML   = '<div class="bs-spinner" style="width:12px;height:12px;border-width:2px;margin:0 auto;"></div>';
          btn.disabled    = true;
        } else if (state === 'success') {
          btn.innerHTML = '✓ Added!';
          btn.disabled  = false;
          btn.style.background = 'var(--green)';
          setTimeout(function () {
            btn.innerHTML = saved;
            btn.style.background = '';
          }, 1800);
        } else {
          btn.innerHTML = saved;
          btn.disabled  = false;
          btn.style.background = '';
        }
        return;
      }
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchFloatCardBtn);
  } else {
    setTimeout(patchFloatCardBtn, 0);
  }
})();

/* ==========================================================================
   CAROUSEL — prev/next arrows, scroll-based opacity, re-init for dynamic content
   ========================================================================== */
(function () {
  'use strict';

  function initCarousel(el) {
    if (el.__carouselInit) return;
    el.__carouselInit = true;

    var track = el.querySelector('.bs-carousel__track');
    var prev  = el.querySelector('.bs-carousel__prev');
    var next  = el.querySelector('.bs-carousel__next');
    if (!track) return;

    function scrollAmt() {
      var card = track.querySelector('.bs-product-card');
      return card ? (card.offsetWidth + 18) * 2 : 340;
    }
    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: -scrollAmt(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: scrollAmt(), behavior: 'smooth' });
    });

    function updateBtns() {
      if (!prev || !next) return;
      var atStart = track.scrollLeft <= 8;
      var atEnd   = track.scrollLeft >= track.scrollWidth - track.clientWidth - 8;
      prev.style.opacity = atStart ? '0.35' : '1';
      prev.disabled      = atStart;
      next.style.opacity = atEnd   ? '0.35' : '1';
      next.disabled      = atEnd;
    }
    track.addEventListener('scroll', updateBtns, { passive: true });
    updateBtns();
  }

  function initAll() {
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose so new carousels added to DOM can be inited
  window.BSCarousel = { initAll: initAll, init: initCarousel };
})();

/* ==========================================================================
   ANCHOR SMOOTH SCROLL — for collection subnav pills
   ========================================================================== */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.bs-anchor-link[data-anchor]');
    if (!link) return;
    e.preventDefault();
    var id  = link.getAttribute('data-anchor');
    var el  = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();

/* ==========================================================================
   ROBLOX USERNAME NOTE — saves to Shopify cart note (shows in admin orders)
   ========================================================================== */
(function () {
  'use strict';

  var _debounceTimer = null;

  function saveNote(username) {
    var note = username ? ('Roblox Username: ' + username) : '';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(function (e) { console.error('BSCartNote', e); });
  }

  function init() {
    var input = document.getElementById('bs-roblox-username');
    if (!input) return;

    // Restore value across cart opens
    var saved = sessionStorage.getItem('bs_roblox_user') || '';
    if (saved) input.value = saved;

    input.addEventListener('input', function () {
      var val = input.value.trim();
      sessionStorage.setItem('bs_roblox_user', val);
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () { saveNote(val); }, 700);
    });

    input.addEventListener('blur', function () {
      clearTimeout(_debounceTimer);
      saveNote(input.value.trim());
    });
  }

  // Run on DOMContentLoaded and re-run after cart opens (input injected lazily)
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('click', function (e) {
    if (e.target.closest('.bs-cart-trigger, [onclick*="BSCart.open"]')) {
      setTimeout(init, 300);
    }
  });
})();

/* ==========================================================================
   SERVICE FEE — auto-add 5% fee product, round up to nearest $0.50
   Settings injected via window.BSServiceFeeSettings in layout/theme.liquid
   ========================================================================== */
var BSServiceFee = (function () {
  'use strict';

  var cfg     = window.BSServiceFeeSettings || {};
  var ENABLED = cfg.enabled !== false;  // default ON unless explicitly disabled
  var HANDLE  = (cfg.handle || 'service-fee').trim();
  var RATE    = parseFloat(cfg.rate) / 100 || 0.05;
  var ROUND   = parseFloat(cfg.round) || 0.5;
  var _lock   = false;
  var _prod   = null;   // cached product JSON

  function isFeeLine(item) { return item.handle === HANDLE; }

  // Calculate fee in cents: 5% of subtotal, rounded up to nearest $0.50
  function calcFeeCents(subtotalCents) {
    var rawDollars     = (subtotalCents / 100) * RATE;
    var roundedDollars = Math.floor(rawDollars / ROUND) * ROUND;
    var feeCents       = Math.round(roundedDollars * 100);
    // Minimum fee of $0.50 — floor can produce $0 for small carts
    return feeCents > 0 ? feeCents : Math.round(ROUND * 100);
  }

  async function getProduct() {
    if (_prod) return _prod;

    // Use server-side injected variants (via Liquid all_products) — no fetch needed
    console.log('[SF] BSServiceFeeVariants=', window.BSServiceFeeVariants);
    if (window.BSServiceFeeVariants && window.BSServiceFeeVariants.length > 0) {
      _prod = { variants: window.BSServiceFeeVariants };
      console.log('[SF] using injected variants:', _prod.variants.length);
      return _prod;
    }
    console.warn('[SF] BSServiceFeeVariants not set - falling back to fetch /products/'+HANDLE+'.js');

    // Fallback: fetch from storefront (requires product published to Online Store)
    try {
      var r = await fetch('/products/' + HANDLE + '.js');
      if (!r.ok) { return null; }
      var data = await r.json();
      if (!data || !data.variants || data.variants.length === 0) { return null; }
      _prod = data;
      return _prod;
    } catch (e) { return null; }
  }

  function findVariant(prod, priceCents) {
    if (!prod || !prod.variants) return null;
    // Shopify storefront /products/handle.js returns price as integer cents (e.g. 50 = $0.50)
    // Guard against string or decimal formats just in case
    return prod.variants.find(function (v) {
      var vp = parseInt(v.price, 10);
      // if stored as dollars decimal (e.g. 0.5 instead of 50), multiply by 100
      if (vp < 10 && priceCents >= 50) vp = Math.round(parseFloat(v.price) * 100);
      return vp === priceCents;
    }) || null;
  }

  // Returns true if cart was actually modified (so caller can re-render)
  async function update(cart) {
    console.log('[SF] update() called. ENABLED='+ENABLED+' _lock='+_lock);
    if (!ENABLED || _lock) { console.log('[SF] skipped: ENABLED='+ENABLED+' lock='+_lock); return false; }
    _lock = true;

    var modified = false;
    try {
      var feeItems      = cart.items.filter(isFeeLine);
      var feeLine       = feeItems[0] || null;
      var eligibleCents = cart.items.reduce(function (s, i) {
        return isFeeLine(i) ? s : s + i.final_line_price;
      }, 0);
      console.log('[SF] eligibleCents='+eligibleCents+' feeItems='+feeItems.length+' handle='+HANDLE);

      // Remove duplicates first (all fee lines after the first)
      for (var d = 1; d < feeItems.length; d++) {
        await shopifyFetch('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: feeItems[d].key, quantity: 0 })
        });
        modified = true;
      }

      // No eligible items → remove fee if present and exit
      if (eligibleCents === 0) {
        if (feeLine) {
          await shopifyFetch('/cart/change.js', {
            method: 'POST',
            body: JSON.stringify({ id: feeLine.key, quantity: 0 })
          });
          modified = true;
        }
        return modified;
      }

      var feeCents = calcFeeCents(eligibleCents);
      var prod     = await getProduct();
      console.log('[SF] getProduct result:', prod ? prod.variants.length+' variants' : 'NULL - product not found!');
      if (!prod) { console.error('[SF] FAIL: could not get fee product. Check handle='+HANDLE); return modified; }

      var variant = findVariant(prod, feeCents);
      console.log('[SF] feeCents='+feeCents+' variant='+JSON.stringify(variant));
      if (!variant) {
        console.error('[SF] FAIL: no variant for '+feeCents+' cents. Available:', prod.variants.map(function(v){return v.price;}));
        return modified;
      }

      // Already correct — nothing to do
      if (feeLine && feeLine.variant_id === variant.id && feeLine.quantity === 1) {
        return modified;
      }

      // Wrong variant → remove it
      if (feeLine && feeLine.variant_id !== variant.id) {
        await shopifyFetch('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: feeLine.key, quantity: 0 })
        });
        feeLine = null;
        modified = true;
      }

      // Add correct variant (or fix qty if somehow > 1)
      if (!feeLine) {
        // Use inventory_policy:continue in payload to bypass deny policy on 0-stock products
        var variantId = String(variant.id); // use string to avoid JS int precision issues
        console.log('[SF] adding variant id='+variantId+' price='+variant.price);
        await shopifyFetch('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: variantId, quantity: 1, properties: { _service_fee: '1' } })
        });
        modified = true;
      } else if (feeLine.quantity !== 1) {
        await shopifyFetch('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: feeLine.key, quantity: 1 })
        });
        modified = true;
      }
    } catch (e) {
      console.warn('[ServiceFee] Update error:', e);
    } finally {
      _lock = false;
    }
    return modified;
  }

  return {
    isFeeLine    : isFeeLine,
    update       : update,
    handle       : HANDLE,
    enabled      : ENABLED,
    calcFeeCents : calcFeeCents
  };
}());

/* ==========================================================================
   DISCOUNT CODE — stores code, patches checkout URL, shows in cart
   ========================================================================== */
var BSDiscount = (function () {
  'use strict';
  var KEY = 'bs_discount_code';

  function getCode() { return localStorage.getItem(KEY) || ''; }
  function setCode(v) { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); }

  function apply() {
    var input = document.getElementById('bs-discount-input');
    var msg   = document.getElementById('bs-discount-msg');
    if (!input) return;
    var code = input.value.trim().toUpperCase();
    setCode(code);
    if (msg) {
      if (code) {
        msg.textContent = 'Code "' + code + '" saved — applied at checkout if valid.';
        msg.className = 'bs-cart-discount__msg bs-cart-discount__msg--ok';
      } else {
        msg.textContent = '';
      }
    }
  }

  function checkout() {
    var code = getCode();
    if (code) {
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
    } else {
      window.location.href = '/checkout';
    }
  }

  // Restore saved code when cart opens
  function restore() {
    var input = document.getElementById('bs-discount-input');
    var code  = getCode();
    if (input && code) {
      input.value = code;
      var msg = document.getElementById('bs-discount-msg');
      if (msg) {
        msg.textContent = 'Code "' + code + '" saved — applied at checkout if valid.';
        msg.className = 'bs-cart-discount__msg bs-cart-discount__msg--ok';
      }
    }
  }

  document.addEventListener('DOMContentLoaded', restore);

  return { apply: apply, checkout: checkout, restore: restore, getCode: getCode };
}());

/* ==========================================================================
   Global event delegation — CSP-safe replacements for inline onclick
   ========================================================================== */
document.addEventListener('DOMContentLoaded', function() {
  // Discount Apply button
  document.addEventListener('click', function(e) {
    if (e.target.closest('#bs-discount-apply-btn')) {
      e.preventDefault();
      if (typeof BSDiscount !== 'undefined') BSDiscount.apply();
    }
    // Checkout handled per-render in setCartState, this is belt+suspenders
    if (e.target.closest('#bs-checkout-btn')) {
      e.preventDefault();
      if (typeof BSDiscount !== 'undefined') BSDiscount.checkout();
      else window.location.href = '/checkout';
    }
  });
});
